import { useCallback } from 'react'
import { useLocation } from 'wouter'
import { useShallow } from 'zustand/react/shallow'

import { View } from '@/lib/api/client'
import { useAppStore, useCADPageStore } from '@/state'
import { useCADQuery } from '../../services/queries/cads'
import { useGetView, useUpdateView } from '../../services/queries/views'
import { useImageData } from '@/pages/CADPage/components/ControlPanel/CreateViewButton/hooks/useImageData'

export const useRetakeView = (viewId?: string | null) => {
  const [, setLocation] = useLocation()
  const { isLoading: isLoadingCad, data: cadData } = useCADQuery()
  const { isLoading: isLoadingView, data: view } = useGetView({ viewId })

  const cadVersionId = cadData?.version?.id
  const documentVersionId = cadData?.documentVersion?.id
  const isLoading = isLoadingCad || isLoadingView

  const imageData = useImageData()
  const serviceWorker = useAppStore((state) => state.serviceWorker)
  const viewWorker = useAppStore(useShallow((state) => state.viewWorker))
  const setCadPageState = useCADPageStore(useShallow((state) => state.setState))
  const retakeLocation = useCADPageStore(
    useShallow((state) => state.retakeLocation),
  )

  // update view onSuccess
  const { mutate: updateView, isPending: isViewPending } = useUpdateView({
    onSuccess: (view?: View) => {
      if (serviceWorker && view && view.download_url) {
        serviceWorker.deleteCache(view.download_url)
      }

      if (viewWorker && cadVersionId && documentVersionId) {
        void viewWorker.kick(cadVersionId, documentVersionId)
      }

      setCadPageState({
        retakeLocation: null,
        isEditModeActive: false,
        isRetakeModeActive: false,
        screenshotPanelOpen: !retakeLocation,
      })

      if (retakeLocation) setLocation(retakeLocation)
    },
  })

  // retake view
  const retakeView = useCallback(() => {
    if (view && view.id && documentVersionId) {
      updateView({
        viewId: view.id,
        values: {
          ...view,
          name: view.name,
          image_data: imageData,
          source_type: 'screenshot',
          document_page_id: view.document_page ?? null,
        },
      })
    }
  }, [imageData, updateView, view, documentVersionId])

  return { isLoading, isViewPending, retakeView }
}
